import React, { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

// Category mapping for retargeting
const PAGE_CATEGORIES = {
  // Capability pages
  '/capabilities/architectural-metal-facades': { category: 'facades', interest: 'Architectural Facades' },
  '/capabilities/interior-metal-decor': { category: 'interiors', interest: 'Interior Metalwork' },
  '/capabilities/special-metal-finishes': { category: 'finishes', interest: 'Special Finishes' },
  '/capabilities/cnc-laser-cutting': { category: 'fabrication', interest: 'CNC & Fabrication' },
  '/capabilities/signage-integration': { category: 'signage', interest: 'Signage & Displays' },
  
  // Solution pages
  '/solutions/facades': { category: 'facades', interest: 'Facade Solutions' },
  '/solutions/interiors': { category: 'interiors', interest: 'Interior Solutions' },
  '/solutions/custom': { category: 'custom', interest: 'Custom Projects' },
  
  // Industry pages
  '/industries/hospitality': { category: 'hospitality', interest: 'Hospitality Projects' },
  '/industries/commercial': { category: 'commercial', interest: 'Commercial Projects' },
  '/industries/residential': { category: 'residential', interest: 'Residential Projects' },
  '/industries/retail': { category: 'retail', interest: 'Retail Projects' },
  
  // Portfolio/Projects
  '/portfolio': { category: 'portfolio', interest: 'Project Gallery' },
  
  // GCC pages
  '/gcc/saudi-arabia': { category: 'gcc', interest: 'Saudi Arabia Market' },
  '/gcc/qatar': { category: 'gcc', interest: 'Qatar Market' },
  '/gcc/uae': { category: 'gcc', interest: 'UAE Market' },
  '/gcc/kuwait': { category: 'gcc', interest: 'Kuwait Market' },
  '/gcc/bahrain': { category: 'gcc', interest: 'Bahrain Market' },
  
  // Africa pages
  '/africa/kenya': { category: 'africa', interest: 'Kenya Market' },
  '/africa/nigeria': { category: 'africa', interest: 'Nigeria Market' },
  '/africa/south-africa': { category: 'africa', interest: 'South Africa Market' },
  '/africa/ethiopia': { category: 'africa', interest: 'Ethiopia Market' },
  '/africa/ghana': { category: 'africa', interest: 'Ghana Market' },
  '/africa/tanzania': { category: 'africa', interest: 'Tanzania Market' },
  
  // UAE Service pages
  '/uae/metal-fabrication-dubai': { category: 'uae-services', interest: 'Metal Fabrication Dubai' },
  '/uae/architectural-facades-dubai': { category: 'uae-services', interest: 'Facades Dubai' },
  '/uae/metal-cladding-uae': { category: 'uae-services', interest: 'Metal Cladding UAE' },
  '/uae/custom-metalwork-dubai': { category: 'uae-services', interest: 'Custom Metalwork Dubai' },
  '/uae/steel-contractors-dubai': { category: 'uae-services', interest: 'Steel Contractors Dubai' },
  '/uae/metal-works-abu-dhabi': { category: 'uae-services', interest: 'Metal Works Abu Dhabi' },
};

// Event types for tracking
const EVENT_TYPES = {
  PAGE_VIEW: 'page_view',
  CATEGORY_INTEREST: 'category_interest',
  FORM_START: 'form_start',
  FORM_SUBMIT: 'form_submit',
  CTA_CLICK: 'cta_click',
  VIDEO_PLAY: 'video_play',
  DOWNLOAD: 'download',
  CONTACT_CLICK: 'contact_click',
};

// User interest storage key
const INTEREST_STORAGE_KEY = 'ihg_user_interests';
const SESSION_ID_KEY = 'ihg_session_id';

// Generate or get session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
};

// Get or initialize user interests
const getUserInterests = () => {
  try {
    const stored = localStorage.getItem(INTEREST_STORAGE_KEY);
    return stored ? JSON.parse(stored) : { categories: {}, pages: [], lastVisit: null };
  } catch {
    return { categories: {}, pages: [], lastVisit: null };
  }
};

// Save user interests
const saveUserInterests = (interests) => {
  try {
    localStorage.setItem(INTEREST_STORAGE_KEY, JSON.stringify(interests));
  } catch (e) {
    console.warn('Could not save user interests:', e);
  }
};

// Track category interest
const trackCategoryInterest = (category, interest) => {
  const userInterests = getUserInterests();
  
  // Increment category count
  userInterests.categories[category] = (userInterests.categories[category] || 0) + 1;
  
  // Add to pages visited (keep last 20)
  userInterests.pages = [
    { page: interest, timestamp: Date.now() },
    ...userInterests.pages.slice(0, 19)
  ];
  
  userInterests.lastVisit = Date.now();
  saveUserInterests(userInterests);
  
  return userInterests;
};

// Get top interests for retargeting
export const getTopInterests = (limit = 3) => {
  const userInterests = getUserInterests();
  const sorted = Object.entries(userInterests.categories)
    .sort(([, a], [, b]) => b - a)
    .slice(0, limit);
  
  return sorted.map(([category, count]) => ({ category, count }));
};

// Check if user is interested in a category
export const isInterestedIn = (category) => {
  const userInterests = getUserInterests();
  return (userInterests.categories[category] || 0) >= 2;
};

// Fire tracking event to analytics platforms
const fireTrackingEvent = (eventType, eventData) => {
  const sessionId = getSessionId();
  const timestamp = Date.now();
  
  const eventPayload = {
    event: eventType,
    sessionId,
    timestamp,
    url: window.location.href,
    referrer: document.referrer,
    ...eventData
  };

  // Console log for debugging (remove in production)
  if (process.env.NODE_ENV === 'development') {
    console.log('[Tracking]', eventType, eventPayload);
  }

  // Google Analytics 4 (gtag)
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventType, {
      ...eventData,
      send_to: 'G-XXXXXXXXXX' // Replace with actual GA4 ID
    });
  }

  // Google Tag Manager dataLayer
  if (window.dataLayer) {
    window.dataLayer.push({
      event: eventType,
      ...eventPayload
    });
  }

  // Facebook Pixel
  if (typeof window.fbq === 'function') {
    if (eventType === EVENT_TYPES.PAGE_VIEW) {
      window.fbq('track', 'PageView');
    } else if (eventType === EVENT_TYPES.FORM_SUBMIT) {
      window.fbq('track', 'Lead', eventData);
    } else if (eventType === EVENT_TYPES.CATEGORY_INTEREST) {
      window.fbq('trackCustom', 'CategoryInterest', eventData);
    }
  }

  // LinkedIn Insight Tag
  if (typeof window.lintrk === 'function') {
    if (eventType === EVENT_TYPES.FORM_SUBMIT) {
      window.lintrk('track', { conversion_id: 'XXXXXX' }); // Replace with actual ID
    }
  }

  // Send to backend for server-side tracking (optional)
  try {
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(eventPayload),
      keepalive: true
    }).catch(() => {}); // Silent fail
  } catch {}
};

// Exported tracking functions
export const trackEvent = (eventType, eventData = {}) => {
  fireTrackingEvent(eventType, eventData);
};

export const trackFormStart = (formName) => {
  trackEvent(EVENT_TYPES.FORM_START, { formName });
};

export const trackFormSubmit = (formName, formData = {}) => {
  trackEvent(EVENT_TYPES.FORM_SUBMIT, { 
    formName, 
    ...formData,
    userInterests: getTopInterests()
  });
};

export const trackCTAClick = (ctaName, ctaLocation) => {
  trackEvent(EVENT_TYPES.CTA_CLICK, { ctaName, ctaLocation });
};

export const trackVideoPlay = (videoTitle, videoCategory) => {
  trackEvent(EVENT_TYPES.VIDEO_PLAY, { videoTitle, videoCategory });
};

export const trackDownload = (fileName, fileType) => {
  trackEvent(EVENT_TYPES.DOWNLOAD, { fileName, fileType });
};

export const trackContactClick = (contactType, location) => {
  trackEvent(EVENT_TYPES.CONTACT_CLICK, { contactType, location });
};

// Main Analytics Provider Component
export function AnalyticsProvider({ children }) {
  const location = useLocation();

  // Track page views and interests
  const trackPageView = useCallback(() => {
    const path = location.pathname;
    const pageInfo = PAGE_CATEGORIES[path];
    
    // Basic page view
    trackEvent(EVENT_TYPES.PAGE_VIEW, {
      page: path,
      title: document.title
    });

    // If this is a categorized page, track interest
    if (pageInfo) {
      const interests = trackCategoryInterest(pageInfo.category, pageInfo.interest);
      
      trackEvent(EVENT_TYPES.CATEGORY_INTEREST, {
        category: pageInfo.category,
        interest: pageInfo.interest,
        visitCount: interests.categories[pageInfo.category],
        topInterests: getTopInterests()
      });
    }

    // Check for dynamic routes
    if (path.startsWith('/capabilities/')) {
      trackCategoryInterest('capabilities', 'Capabilities');
    } else if (path.startsWith('/portfolio/')) {
      trackCategoryInterest('portfolio', 'Projects');
    } else if (path.startsWith('/studio-works/')) {
      trackCategoryInterest('studio', 'Studio Works');
    }
  }, [location.pathname]);

  useEffect(() => {
    trackPageView();
  }, [trackPageView]);

  return <>{children}</>;
}

// Hook to get user interests
export const useUserInterests = () => {
  return {
    interests: getUserInterests(),
    topInterests: getTopInterests(),
    isInterestedIn
  };
};

export default AnalyticsProvider;
